import {Injectable} from '@angular/core';
import {
  AnnotationsDataV2Service
} from '@app/components/projects/surveys/annotation/services/annotations-data-v2.service';
import {
  AnnotationsUpdateV2Service
} from '@app/components/projects/surveys/annotation/services/annotation-update-v2.service';
import {BehaviorSubject, combineLatest, EMPTY, of, Subscription, switchMap} from 'rxjs';
import {
  ANNOMAINKEY,
  IACategory,
  ICategories,
  ICompiledCategory
} from '@app/components/projects/surveys/annotation/store/annotations.entitiy';
import {cloneDeep, isEqual} from 'lodash';
import {IQuestionFilterExprCategory} from '@app/components/projects/surveys/annotation/store/models/annotationState';
import {nanoid} from 'nanoid';
import dotProp from 'dot-prop-immutable';
import {moveItemInArray} from '@angular/cdk/drag-drop';
import {
  AnnotationsRealtimeService
} from '@app/components/projects/surveys/annotation/services/annotations-realtime.service';

@Injectable({
  providedIn: 'root'
})
export class CategoriesManagerService {

  subscriptions: Subscription;

  catQuestionFilterExpr$ = new BehaviorSubject<IQuestionFilterExprCategory>({
    key: null,
    statementKey: null,
    rowKey: null
  });
  filteredAnnoForTreeView$ = new BehaviorSubject<Array<IACategory>>(null);
  treeViewOrder: 'asc' | 'desc' = 'desc';
  annoCompiledForCat$: Record<string, BehaviorSubject<Record<string, { count: number, user: Set<number> }>>> = {};
  categoryCompiled$: Record<string, BehaviorSubject<ICompiledCategory>> = {};
  categories$: Record<string, BehaviorSubject<ICategories>> = {};
  compiledSubs$: Record<string, boolean> = {};
  questionObs: any;
  categoryAllSet$ = new BehaviorSubject<boolean>(false);

  constructor(
    private annotationsDataService: AnnotationsDataV2Service,
    private annotationsUpdateV2Service: AnnotationsUpdateV2Service,
    private annoRealTimeService: AnnotationsRealtimeService
  ) {
  }

  get actualCategories() {
    return this.selectActualCategory(this.categories$[this.catQuestionFilterExpr$.value.key]?.value);
  }

  setCategoriesFromAuto(data, qKey: string) {
    const category = Object.entries<Array<string>>(data.question)
      .reduce((acc, [label, value]) => {
        const id = `cat_${nanoid(7)}`;
        acc[ANNOMAINKEY].childIndex.push(id);
        const child = value.reduce((acc2, label2) => {
          const id2 = `cat_${nanoid(7)}`;
          const ref = this.annotationsDataService.getKeyFromLabel(label2, qKey);
          return {...acc2, [id2]: {id: id2, pId: id, label: label2, childIndex: [], ref}};
        }, {}) || {};
        return {...acc, [id]: {id, pId: ANNOMAINKEY, label, childIndex: Object.keys(child)}, ...child};
      }, {mainRootxyz: {id: ANNOMAINKEY, pId: null, label: 'Main Root', childIndex: []}});
    this.setActualCategories(category);
  }

  deleteAllCategories(){
    const qFilterExpr = this.catQuestionFilterExpr$.value;
    this.annoRealTimeService.deleteAllCategories(qFilterExpr.key)
      .then(_ => this.categories$[qFilterExpr.key].next({}));
  }

  sortTreeView(type: 'occ' | 'name' = 'occ') {
    const toSort = cloneDeep(this.filteredAnnoForTreeView$.value);
    const order = this.treeViewOrder === 'desc' ? 'asc' : 'desc';
    const sort = (a: IACategory, b: IACategory) => {
      if (type === 'occ') {
        const compiled = this.annoCompiledForCat$[this.catQuestionFilterExpr$.value.key].value;
        return (order === 'asc') ? compiled[a.id].count - compiled[b.id].count : compiled[b.id].count - compiled[a.id].count;
      }
      const dict = this.annotationsDataService.actualDictionary$.value;
      return (order === 'asc') ? dict[a.id].label.localeCompare(dict[b.id].label) : dict[b.id].label.localeCompare(dict[a.id].label);
    };
    toSort?.sort(sort);
    this.treeViewOrder = order;
    this.filteredAnnoForTreeView$.next(toSort);
  }

  selectActualCategory(allCatForQuestion: ICategories = this.categories$[this.catQuestionFilterExpr$.value.key]?.value): Record<string, IACategory> {
    const filterDef = this.catQuestionFilterExpr$.value;
    const mainCat = {
      [ANNOMAINKEY]:
        {id: ANNOMAINKEY, pId: null, label: 'Main Root', childIndex: [], expanded: true, visible: false}
    } as Record<string, IACategory>;
    if (allCatForQuestion && filterDef.key) {
      if (filterDef.statementKey) {
        if (filterDef.rowKey) {
          return allCatForQuestion.cell?.[filterDef.rowKey]?.[filterDef.statementKey] || mainCat;
        }
        return allCatForQuestion.statement?.[filterDef.statementKey] || mainCat;
      } else if (filterDef.rowKey) {
        return allCatForQuestion.row?.[filterDef.rowKey] || mainCat;
      }
      return allCatForQuestion.question || mainCat;
    }
    return mainCat;
  }

  setActualCategories(newValue: Record<string, IACategory>, allCatForQuestion: ICategories = null): void {
    const filterDef = this.catQuestionFilterExpr$.value;

    if (filterDef.key) {
      allCatForQuestion = cloneDeep(this.categories$[this.catQuestionFilterExpr$.value.key].value);
    }

    allCatForQuestion = cloneDeep(allCatForQuestion);
    if (filterDef.key) {
      if (filterDef.statementKey) {
        if (filterDef.rowKey) {
          allCatForQuestion = dotProp.set(allCatForQuestion, `cell.${filterDef.rowKey}.${filterDef.statementKey}`, newValue);
        }
        allCatForQuestion = dotProp.set(allCatForQuestion, `statement.${filterDef.statementKey}`, newValue);
      } else if (filterDef.rowKey) {
        allCatForQuestion = dotProp.set(allCatForQuestion, `row.${filterDef.rowKey}`, newValue);
      }
      allCatForQuestion = dotProp.set(allCatForQuestion, `question`, newValue);
      this.annoRealTimeService.setCategoriesToFB(allCatForQuestion, filterDef.key);
      this.resetTreeView();
    }
  }

  questionObservables(questionKey: string) {
    this.categories$[questionKey] = this.annoRealTimeService.setupCategoriesForQuestion(questionKey);
    this.annoCompiledForCat$[questionKey] ??= new BehaviorSubject(null);
    this.questionObs?.unsubscribe();
    delete this.questionObs;
    delete this.categoryCompiled$[questionKey];
    this.questionObs ??= combineLatest([this.categories$[questionKey], this.annoCompiledForCat$[questionKey]])
      .pipe(switchMap(data => data.every(d => d) ? of(data) : EMPTY))
      .subscribe(([categories, compiled]) => {
        this.categoryCompiled$[questionKey] ??= new BehaviorSubject(null);
        const actualCategories = this.selectActualCategory(categories);
        const addChild = (cat: IACategory) => {
          if (cat?.ref && !cat?.childIndex?.length) {
            return {nbOcc: compiled[cat.ref]?.count || 0, nbUser: compiled[cat.ref]?.user || new Set()};
          }
          return (cat?.childIndex || []).reduce((acc, curr) => {
            if (actualCategories[curr]?.ref && !actualCategories[curr]?.childIndex?.length) {
              acc.nbOcc += compiled[actualCategories[curr].ref]?.count || 0;
              acc.nbUser = new Set([...acc.nbUser, ...(compiled[actualCategories[curr].ref]?.user || [])]);
            } else {
              const child = addChild(actualCategories[curr]);
              acc.nbOcc += child.nbOcc;
              acc.nbUser = new Set([...acc.nbUser, ...child.nbUser]);
            }
            return acc;
          }, {nbOcc: 0, nbUser: new Set([])});
        };
        const catCompiled = Object.values(actualCategories || {})
          .reduce((obj, cat) => {
            const child = addChild(cat);
            return {
              ...obj,
              nbOcc: {...obj.nbOcc, [cat.id]: child.nbOcc},
              nbUser: {...obj.nbUser, [cat.id]: child.nbUser}
            };
          }, {nbOcc: {}, nbUser: {}});
        if (!isEqual(catCompiled, this.annoCompiledForCat$[questionKey]?.value)) {
          this.categoryCompiled$[questionKey].next(catCompiled);
        }
      });
    this.subscriptions.add(this.questionObs);
    return true;
  }

  setObservables() {
    this.subscriptions = new Subscription();
    this.subscriptions.add(combineLatest([this.annotationsDataService.questionFilterExpr$, this.annotationsDataService.dictionary$])
      .pipe(switchMap(data => data.every(d => d) ? of(data) : EMPTY))
      .subscribe(([filterDef, dictionary]) => {
        this.categories$[filterDef.key] ??= new BehaviorSubject({});
        let catFilter;
        if (Object.keys(dictionary?.[filterDef?.key] || {}).length) {
          catFilter = {...this.catQuestionFilterExpr$.value, key: filterDef.key};
        } else {
          catFilter = {key: Object.keys(dictionary || {}).find(d => Object.keys(dictionary[d] || {}).length)};
        }
        if (!isEqual(catFilter, this.catQuestionFilterExpr$.value)) {
          this.catQuestionFilterExpr$.next(catFilter);
        }
      }));
    this.subscriptions.add(combineLatest([this.annotationsDataService.filteredAnnoList$])
      .pipe(switchMap(data => data.every(a => a) ? of(data) : EMPTY))
      .subscribe(([filteredAnno]) => {
        this.filteredAnnoForTreeView$.next(Object.values(filteredAnno).map((anno) => {
          return {id: anno.key, pId: 'mainRootxyz', label: anno.label};
        }).filter(Boolean));
      }));
    this.subscriptions.add(this.catQuestionFilterExpr$
      .subscribe((filterDef) => {
        if (filterDef.key) {
          this.questionObservables(filterDef.key);
          this.annotationsDataService.setQuestionFilterExpr({
            key: filterDef.key,
            statementKey: [filterDef.statementKey].filter(Boolean),
            rowKey: [filterDef.rowKey].filter(Boolean)
          });
        }
      }));
    this.subscriptions.add(combineLatest([this.annotationsDataService.compiledAnnotations$, this.catQuestionFilterExpr$])
      .pipe(switchMap(data => data.every(d => d) ? of(data) : EMPTY))
      .subscribe(([compiled, filterDef]) => {
        const [allRows, allStatements] = [!filterDef.rowKey, !filterDef.statementKey];
        this.annoCompiledForCat$[filterDef.key] ??= new BehaviorSubject({} as Record<string, {
          count: number,
          user: Set<number>
        }>);
        const compiledForCat = Object.keys(compiled[filterDef.key] || {}).reduce((total, rKey) => {
          return (allRows || filterDef.rowKey === rKey) ? Object.keys(compiled[filterDef.key][rKey] || {})
            .reduce((totRow, sKey) => {
              return (allStatements || filterDef.statementKey === sKey) ?
                Object.keys(compiled[filterDef.key][rKey][sKey]).reduce((totStatement, aKey) => {
                  totStatement[aKey] ??= {count: 0, user: new Set()};
                  totStatement[aKey].count += compiled[filterDef.key][rKey][sKey][aKey].count;
                  totStatement[aKey].user = new Set([...totStatement[aKey].user, ...compiled[filterDef.key][rKey][sKey][aKey].user]);
                  return totStatement;
                }, total) : totRow;
            }, total) : total;
        }, {});
        if (!isEqual(compiledForCat, this.annoCompiledForCat$[filterDef.key]?.value)) {
          this.annoCompiledForCat$[filterDef.key].next(compiledForCat);
        }
      }));
  }

  destroyObservables() {
    this.subscriptions.unsubscribe();
    this.compiledSubs$ = {};
    this.annoCompiledForCat$ = {};
    this.categoryCompiled$ = {};
    delete this.subscriptions;
  }

  setCategories(categories: any, noUpdate?: boolean) {
    const newModel = noUpdate ? categories : this.annotationsUpdateV2Service.updateCategories(categories);
    Object.keys(newModel || {}).forEach((key) => {
      this.annoRealTimeService.setCategoriesToFB(newModel[key], key);
    });
    this.categoryAllSet$.next(true);
  }

  getCategoriesToSave(){
    return Object.keys(this.categories$).reduce((acc, curr) =>
      ({...acc , [curr]: this.categories$[curr].value}), {});
  }

  changeQuestionFilterExpr(key: string, props: 'key' | 'statementKey' | 'rowKey') {
    const questionFilterExpr = cloneDeep(this.catQuestionFilterExpr$.value);
    questionFilterExpr[props] = key;
    if (props === 'key') {
      questionFilterExpr.statementKey = null;
      questionFilterExpr.rowKey = null;
    }
    if (!isEqual(questionFilterExpr, this.catQuestionFilterExpr$.value)) {
      this.catQuestionFilterExpr$.next(questionFilterExpr);
    }
  }

  addNewCategory(parent: string, params: { label?: string, ref?: string, index?: number }) {
    const qKey = this.catQuestionFilterExpr$.value.key;
    const actualCategories = cloneDeep(this.categories$[qKey].value);
    const toUse = this.selectActualCategory(actualCategories);
    toUse[parent].childIndex ??= [];
    if (toUse[parent].ref) {
      const newId2 = `cat_${nanoid(7)}`;
      toUse[newId2] = {id: newId2, pId: parent, label: toUse[parent].label, childIndex: [], ref: toUse[parent].ref};
      toUse[parent].childIndex.push(newId2);
      toUse[parent].ref = null;
    }
    const newId = `cat_${nanoid(7)}`;
    params.label = params.ref ? this.annotationsDataService.actualDictionary$.value[params.ref].label : params.label.trim();
    toUse[newId] = {id: newId, pId: parent, label: params.label, childIndex: [], ref: params.ref};
    if (params.index !== undefined) {
      toUse[parent].childIndex.splice(params.index, 0, newId);
    } else {
      toUse[parent].childIndex.push(newId);
    }
    this.setActualCategories(toUse);
    if (params.ref) {
      this.resetTreeView();
    }
  }


  addNewCategory2(params, sourceData, parentKey, nodeAbove ) {
    const qKey = this.catQuestionFilterExpr$.value.key;
    const actualCategories = cloneDeep(this.categories$[qKey].value);
    const toUse = this.selectActualCategory(actualCategories);
    const newId = `cat_${nanoid(7)}`;
    params.label = params.ref ? this.annotationsDataService.actualDictionary$.value[params.ref].label : params.label.trim();
    if (nodeAbove && nodeAbove.isExpanded) {
      toUse[newId] = {id: newId, pId: nodeAbove.data.id, label: sourceData.label, childIndex: [], ref: params.ref};
    } else {
      toUse[newId] = {id: newId, pId: parentKey, label: sourceData.label, childIndex: [], ref: params.ref};
    }
    toUse[parentKey].childIndex ??= [];
    if (!nodeAbove) {
      toUse[parentKey].childIndex.splice(0, 0, newId);
    } else {
      if (nodeAbove.isExpanded) {
        toUse[nodeAbove.data.id].childIndex.splice(0, 0, newId);
      } else {
        if (parentKey === ANNOMAINKEY) {
          toUse[parentKey].childIndex.splice(params.index, 0, newId);
        } else {
          const realIndex = toUse[parentKey].childIndex.findIndex(elt => elt === nodeAbove.key);
          toUse[parentKey].childIndex.splice(realIndex + 1, 0, newId);
        }
      }
    }
    this.setActualCategories(toUse);
    if (params.ref) {
      this.resetTreeView();
    }
  }

  reorderCategory(id: string, newParent: string, newIndex?: number) {
    const toUse = cloneDeep(this.selectActualCategory());
    const getParents = (cat: string): Array<string> =>
      (toUse[cat].pId) ? [toUse[cat].pId, ...getParents(toUse[cat].pId)] : [];
    if (![...getParents(newParent), newParent].includes(id)) {
      const pId = toUse[id].pId;
      if (toUse[newParent].ref) {
        const newId2 = `cat_${nanoid(7)}`;
        toUse[newId2] = {
          id: newId2, pId: newParent, label: toUse[newParent].label, childIndex: [], ref: toUse[newParent].ref
        };
        toUse[newParent].childIndex ??= [];
        toUse[newParent].childIndex.push(newId2);
        toUse[newParent].ref = null;
      }
      toUse[pId].childIndex = toUse[pId].childIndex.filter(c => c !== id);
      newIndex ??= toUse[newParent].childIndex.length;
      toUse[newParent].childIndex.splice(newIndex, 0, id);
      toUse[id].pId = newParent;
      this.setActualCategories(toUse);
      this.resetTreeView();
    }
  }

  renameCategory(id: string, label: string) {
    const qKey = this.catQuestionFilterExpr$.value.key;
    const actualCategories = cloneDeep(this.categories$[qKey].value);
    const toUse = this.selectActualCategory(actualCategories);
    toUse[id].label = label;
    if (toUse[id].ref) {
      this.annotationsDataService.renameAnno(toUse[id].ref, label);
    }
    this.annoRealTimeService.setCategoriesToFB(actualCategories, qKey);
  }

  deleteCategory(id: string) {
    const toUse = this.selectActualCategory();
    const deleteChildren = (cat: string) => {
      toUse[cat].childIndex?.forEach(c => {
        if (!toUse[c].childIndex?.length) {
          deleteChildren(c);
        }
        delete toUse[c];
      });
      delete toUse[cat];
    };
    toUse[id]?.childIndex?.forEach(c => deleteChildren(c));
    const parent = toUse[id].pId;
    toUse[parent].childIndex = toUse[parent].childIndex?.filter(c => c !== id);
    delete toUse[id];
    this.setActualCategories(toUse);
    this.resetTreeView();
  }

  moveKeywordInTreeview(id: string, asideOf?: string) {
    const keyWords = cloneDeep(this.filteredAnnoForTreeView$.value);
    const indexOf = keyWords.findIndex(k => k.id === id);
    const indexTo = asideOf ? keyWords.findIndex(k => k.id === asideOf) : 0;
    moveItemInArray(keyWords, indexOf, indexTo);
    this.filteredAnnoForTreeView$.next(keyWords);
  }

  resetAllCategoriesFromView() {
    this.setActualCategories(null);
    this.resetTreeView();
  }

  resetTreeView() {
    if (this.catQuestionFilterExpr$.value.key) {
      const temp2 = cloneDeep(this.categories$[this.catQuestionFilterExpr$.value.key].value);
      this.categories$[this.catQuestionFilterExpr$.value.key].next({});
      this.categories$[this.catQuestionFilterExpr$.value.key].next(temp2);
      const temp = cloneDeep(this.filteredAnnoForTreeView$.value);
      this.filteredAnnoForTreeView$.next([]);
      this.filteredAnnoForTreeView$.next(temp);
    }
  }

}
